import { useState, useEffect, useRef, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '@/api/client';
import type { Scrapbook } from '@shared/types';

interface CreateScrapbookModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate?: (scrapbook: Scrapbook) => void;
}

export function CreateScrapbookModal({
  isOpen,
  onClose,
  onCreate,
}: CreateScrapbookModalProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const titleInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Focus title input when modal opens
  useEffect(() => {
    if (isOpen && titleInputRef.current) {
      titleInputRef.current.focus();
    }
  }, [isOpen]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setTitle('');
      setDescription('');
      setError(null);
      setIsCreating(false);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && !isCreating) {
        onClose();
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = '';
      };
    }
  }, [isOpen, isCreating, onClose]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setError('Title is required');
      return;
    }

    if (trimmedTitle.length > 255) {
      setError('Title must be 255 characters or less');
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      const scrapbook = await api.post<Scrapbook>('/scrapbooks', {
        title: trimmedTitle,
        description: description.trim() || undefined,
      });

      // Validate response has a valid scrapbook with an id
      if (scrapbook?.id) {
        onCreate?.(scrapbook);
        onClose();
        navigate(`/editor/${scrapbook.id}`);
      } else {
        // Handle invalid response - scrapbook may have been created but response was malformed
        setError('Scrapbook created but response was invalid. Please refresh the page.');
        setIsCreating(false);
      }
    } catch (err) {
      if (err instanceof ApiError && err.data && typeof err.data === 'object' && 'message' in err.data) {
        setError(err.data.message as string);
      } else {
        setError('Failed to create scrapbook. Please try again.');
      }
      setIsCreating(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isCreating) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="modal-backdrop"
      onClick={handleBackdropClick}
      role="presentation"
    >
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-scrapbook-title"
      >
        <div className="modal-header">
          <h2 id="create-scrapbook-title" className="modal-title">
            Create New Scrapbook
          </h2>
          <button
            className="modal-close"
            onClick={onClose}
            disabled={isCreating}
            aria-label="Close"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M15 5L5 15M5 5L15 15"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-field">
              <label htmlFor="scrapbook-title" className="form-label">
                Title <span className="form-required">*</span>
              </label>
              <input
                ref={titleInputRef}
                id="scrapbook-title"
                type="text"
                className="form-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="My Scrapbook"
                maxLength={255}
                disabled={isCreating}
                aria-describedby={error ? 'create-error' : undefined}
              />
              <p className="form-hint">
                {title.length}/255 characters
              </p>
            </div>

            <div className="form-field">
              <label htmlFor="scrapbook-description" className="form-label">
                Description <span className="form-optional">(optional)</span>
              </label>
              <textarea
                id="scrapbook-description"
                className="form-textarea"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="A brief description of your scrapbook..."
                maxLength={1000}
                rows={3}
                disabled={isCreating}
              />
              <p className="form-hint">
                {description.length}/1000 characters
              </p>
            </div>

            {error && (
              <p id="create-error" className="form-error" role="alert">
                {error}
              </p>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={isCreating}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isCreating || !title.trim()}
            >
              {isCreating ? (
                <>
                  <span className="btn-spinner" aria-hidden="true" />
                  Creating...
                </>
              ) : (
                'Create Scrapbook'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
